import { useState, createContext, useContext, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, ChevronRight, ChevronLeft, Play, Pause, RotateCcw, Layers } from "lucide-react";

// ── Multiple Demo Tour Paths ──
const tourPaths = {
  flagship: {
    id: 'flagship', title: 'Flagship: Blocked stage to trade release',
    summary: 'Oakline Custom Homes: 12 Smith St Pre-Slab blocked, resolved, trade unlocked.',
    steps: [
      { path: '/builder', title: 'Command Centre — Project at risk', description: '12 Smith St is at risk. Pre-Slab is blocked by 3 conditions. This is where the PM starts every day.', tag: 'The problem' },
      { path: '/builder/stage-readiness', title: 'Pre-Slab is not ready', description: 'Soil report overdue. Stormwater pending. Drainage cert under review. Trade locked. Surveyor cannot be notified.', tag: 'Stage gate' },
      { path: '/builder/proof-review', title: 'Evidence arriving — AI checks quality', description: 'David Patel uploaded a drainage certificate. AI matched it at 87% confidence. Approve it and the condition clears.', tag: 'Evidence' },
      { path: '/builder/whatsapp-action-rail', title: 'Chase the blocker via WhatsApp', description: 'David Patel is overdue on the soil report. Send a chase. AI extracts replies back into the job.', tag: 'Chase' },
      { path: '/builder/trade-release', title: 'Trades wait until the stage is safe', description: 'Concreter on hold. As blockers clear, trades move from Hold to Ready to Released.', tag: 'Trades' },
      { path: '/builder/release-calendar', title: 'Calendar shows the real timeline', description: 'Release windows, inspections, and trade mobilisation tied to readiness. Risk visible.', tag: 'Calendar' },
      { path: '/builder/readiness-dossier', title: 'Generate the readiness pack', description: 'All conditions, evidence, blockers compiled. Export PDF or send to surveyor.', tag: 'Dossier' },
      { path: '/shared/client-portal', title: 'Client sees a curated update', description: 'AI drafts a weekly update from live state. Builder reviews. Client never sees blockers.', tag: 'Client' },
    ],
  },
  'create-to-readiness': {
    id: 'create-to-readiness', title: 'Full journey: Create project to readiness',
    summary: 'Start from project creation through to the readiness workflow.',
    steps: [
      { path: '/builder/project-intake', title: 'Create a new project', description: 'Enter the site address, select project type, and configure the operating model.', tag: 'Create' },
      { path: '/builder/project-intake', title: 'Site intelligence detected', description: 'AI detects council, parcel, zone, and planning context from the address.', tag: 'Site intel' },
      { path: '/builder', title: 'Project appears in command centre', description: 'The new project joins the workspace. Initial status, people, and scope are configured.', tag: 'Overview' },
      { path: '/builder/decision-import', title: 'Import the permit decision', description: 'Upload the decision letter. AI extracts conditions across all stages.', tag: 'Import' },
      { path: '/builder/stage-conditions', title: 'Conditions extracted per stage', description: 'Each condition is assigned an owner, due date, and evidence requirement.', tag: 'Conditions' },
      { path: '/builder/stage-readiness', title: 'Readiness engine activated', description: 'Stage gates show which stages are ready and which have blockers.', tag: 'Readiness' },
      { path: '/builder/trade-release', title: 'Trade release linked to readiness', description: 'Trades cannot be released until their stage conditions are cleared.', tag: 'Trades' },
    ],
  },
  'weak-proof': {
    id: 'weak-proof', title: 'Weak proof / re-upload story',
    summary: 'What happens when uploaded evidence does not meet quality standards.',
    steps: [
      { path: '/builder/proof-review', title: 'Evidence uploaded but flagged', description: 'A site photo was uploaded for sediment controls, but AI detected low quality. Needs re-upload.', tag: 'Quality' },
      { path: '/builder/whatsapp-action-rail', title: 'Request better photos via WhatsApp', description: 'Send a specific request to the site supervisor with guidance on what is needed.', tag: 'Request' },
      { path: '/builder/stage-readiness', title: 'Condition still pending', description: 'The condition cannot clear until the evidence passes review.', tag: 'Impact' },
      { path: '/builder/proof-review', title: 'Re-upload arrives, approved', description: 'Better photos arrive via QR upload. AI matches them. Human approves. Condition clears.', tag: 'Resolved' },
    ],
  },
  'owner-supplied': {
    id: 'owner-supplied', title: 'Client-supplied finishes / owner trades',
    summary: 'How easypermit tracks scope boundaries and owner-side dependencies.',
    steps: [
      { path: '/builder/scope-boundary-ledger', title: 'Scope boundary is clear', description: 'Builder scope, excluded items, owner-supplied materials, and owner-appointed trades all tracked.', tag: 'Scope' },
      { path: '/builder/stage-readiness', title: 'Owner items can block stages', description: 'If client tiles are late, the tiler cannot start, and the stage slips.', tag: 'Risk' },
      { path: '/shared/client-portal', title: 'Client sees their responsibilities', description: 'The client portal shows which items the client needs to action.', tag: 'Client' },
    ],
  },
  'buyer-handover': {
    id: 'buyer-handover', title: 'Spec home / buyer handover',
    summary: 'How a completed spec home is handed over to the buyer.',
    steps: [
      { path: '/shared/buyer-handover', title: 'Buyer handover pack', description: 'Manuals, certificates, room guides, and system instructions compiled for the buyer.', tag: 'Pack' },
      { path: '/shared/buyer-handover', title: 'Room-by-room experience', description: 'Each room and system has care instructions, appliance details, and warranty info.', tag: 'Experience' },
      { path: '/shared/buyer-handover', title: 'Issue triage', description: 'Buyer can flag defects or questions. Builder triages and responds.', tag: 'Support' },
    ],
  },
  'trade-unlock': {
    id: 'trade-unlock', title: 'Trade release unlock story',
    summary: 'Watch blockers clear and trades move from Hold to Released.',
    steps: [
      { path: '/builder/trade-release', title: 'Concreter is on hold', description: '3 blockers preventing release. Delay cost exposure: $12k-$18k.', tag: 'Locked' },
      { path: '/builder/job-blocker', title: 'Resolve the critical blocker', description: 'Clear the soil report verification. This is the highest-impact action.', tag: 'Action' },
      { path: '/builder/stage-readiness', title: 'Stage readiness updates', description: 'One fewer blocker. Readiness percentage improves. Getting closer.', tag: 'Progress' },
      { path: '/builder/trade-release', title: 'Trade moves to Ready', description: 'When all blockers clear, the trade moves from Hold to Ready to Release.', tag: 'Unlocked' },
      { path: '/builder/release-calendar', title: 'Calendar shows the window', description: 'The concreter release window is visible. Mobilisation can be scheduled.', tag: 'Scheduled' },
    ],
  },
  'hero-flow': {
    id: 'hero-flow', title: 'Lot 42 Mambourin — Full Trade Flow',
    summary: 'The hero demo: Trade Release → Generate Pass → Check In → Cannot Start → Resolve → Sign Off → Dossier.',
    steps: [
      { path: '/builder/stage-readiness', title: 'Stage Readiness — Pre-Slab Gate', description: 'Lot 42: 3/4 conditions cleared. Waterproofer readiness checks show partial status.', tag: 'Readiness' },
      { path: '/builder/trade-release', title: 'Waterproofer is ready to release', description: 'AquaSeal Waterproofing ready. Generate the SiteReady Trade Pass.', tag: 'Ready' },
      { path: '/builder/trade-release', title: 'SiteReady Trade Pass generated', description: 'Pass created with site notes, conditions, safety brief, and check-in.', tag: 'Pass' },
      { path: '/builder/trade-pass/trade-042-wp', title: 'Tradie opens the SiteReady link', description: 'Marco Vella sees the mobile Trade Pack with check-in button.', tag: 'Mobile' },
      { path: '/builder/trade-pass/trade-042-wp', title: 'Waterproofer checks in — 3 crew', description: 'On-Site Now and Client Portal update instantly across the system.', tag: 'Check In' },
      { path: '/builder', title: 'Command Centre — On-Site Now', description: '2 trades on site. 8 total crew. Live attendance visible.', tag: 'Overview' },
      { path: '/shared/client-portal', title: 'Client sees trades working', description: 'Clean, client-safe view. No internal details exposed.', tag: 'Client' },
      { path: '/builder/trade-release', title: 'Cannot Start — proof missing', description: 'Marco reports waterproofing spec not approved. Blocker created.', tag: 'Blocked' },
      { path: '/builder/job-blocker', title: 'Job Blocker board updates', description: 'Cannot Start blocker appears. Supervisor notified immediately.', tag: 'Blocker' },
      { path: '/builder/trade-release', title: 'Supervisor resolves the issue', description: 'Spec approved. Cannot Start lifted. Trade can proceed.', tag: 'Resolved' },
      { path: '/builder/trade-release', title: 'Work proof uploaded', description: 'Waterproofing photo: membrane applied per specification.', tag: 'Proof' },
      { path: '/builder/proof-review', title: 'Proof appears in review queue', description: 'Supervisor can approve, flag, or request re-upload.', tag: 'Review' },
      { path: '/builder/trade-release', title: 'Trade signed off', description: 'Waterproofer signed off. Evidence recorded in the system.', tag: 'Sign Off' },
      { path: '/builder/readiness-dossier', title: 'Readiness Dossier updated', description: 'Work proof feeds into the dossier. Full audit trail complete.', tag: 'Dossier' },
    ],
  },
};

const TourContext = createContext({ startTour: () => {}, isTourActive: false, isPitchMode: false });
export function useTour() { return useContext(TourContext); }

export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [pitchMode, setPitchMode] = useState(false);
  const [pitchPaused, setPitchPaused] = useState(false);
  const [activePath, setActivePath] = useState('flagship');
  const [stepIndex, setStepIndex] = useState(0);
  const [showSelector, setShowSelector] = useState(false);
  const pitchTimer = useRef(null);
  const navigate = useNavigate();

  const tour = tourPaths[activePath] || tourPaths.flagship;
  const step = tour.steps[stepIndex];

  const startTour = useCallback((pathId) => {
    const id = pathId || 'flagship';
    setActivePath(id);
    setStepIndex(0);
    setActive(true);
    setPitchMode(false);
    navigate(tourPaths[id].steps[0].path);
  }, [navigate]);

  const startPitch = useCallback(() => {
    setActivePath('flagship');
    setStepIndex(0);
    setActive(true);
    setPitchMode(true);
    setPitchPaused(false);
    navigate(tourPaths.flagship.steps[0].path);
  }, [navigate]);

  const endTour = useCallback(() => {
    setActive(false); setPitchMode(false); setPitchPaused(false); setStepIndex(0);
    if (pitchTimer.current) clearTimeout(pitchTimer.current);
  }, []);

  const goNext = useCallback(() => {
    if (stepIndex < tour.steps.length - 1) {
      const next = stepIndex + 1;
      setStepIndex(next);
      navigate(tour.steps[next].path);
    } else { endTour(); }
  }, [stepIndex, tour, navigate, endTour]);

  const goPrev = useCallback(() => {
    if (stepIndex > 0) {
      const prev = stepIndex - 1;
      setStepIndex(prev);
      navigate(tour.steps[prev].path);
    }
  }, [stepIndex, tour, navigate]);

  // Pitch mode auto-advance
  useEffect(() => {
    if (pitchMode && active && !pitchPaused) {
      pitchTimer.current = setTimeout(() => goNext(), 5000);
      return () => clearTimeout(pitchTimer.current);
    }
  }, [pitchMode, active, pitchPaused, stepIndex, goNext]);

  return (
    <TourContext.Provider value={{ startTour, startPitch, isTourActive: active, isPitchMode: pitchMode, activeLens: null }}>
      {children}
      {/* Tour selector modal */}
      {showSelector && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowSelector(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="tour-selector">
            <div className="bg-slate-900 px-5 py-4">
              <h3 className="text-base font-semibold text-white">Choose a demo path</h3>
              <p className="text-xs text-slate-400">Each path tells a different product story.</p>
            </div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {Object.values(tourPaths).map(t => (
                <button key={t.id} onClick={() => { setShowSelector(false); startTour(t.id); }}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/30 transition-all group"
                  data-testid={`tour-path-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600">{t.title}</p>
                    <Badge className="text-[9px] bg-slate-100 text-slate-600">{t.steps.length} steps</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{t.summary}</p>
                </button>
              ))}
              <button onClick={() => { setShowSelector(false); startPitch(); }}
                className="w-full text-left p-3 rounded-xl border-2 border-indigo-200 bg-indigo-50/50 hover:border-indigo-300 transition-all group"
                data-testid="start-pitch-mode">
                <div className="flex items-center gap-2">
                  <Play className="w-4 h-4 text-indigo-600" />
                  <p className="text-sm font-bold text-indigo-800">Pitch Mode (auto-play)</p>
                </div>
                <p className="text-xs text-indigo-600 mt-0.5">Auto-runs the flagship story with timed transitions. For investor meetings.</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tour bar */}
      {active && step && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] flex justify-center px-4 pb-5 pointer-events-none" data-testid="live-demo-bar">
          <div className="pointer-events-auto bg-slate-900 text-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden tour-bar-enter">
            <div className="flex gap-1 px-5 pt-3">
              {tour.steps.map((_, i) => (
                <div key={i} className={`h-1 rounded-full flex-1 transition-all duration-300 ${i <= stepIndex ? 'bg-blue-400' : 'bg-slate-700'}`} />
              ))}
            </div>
            <div className="px-5 pt-2.5 pb-3.5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider">
                      {pitchMode ? 'Pitch Mode' : tour.title} — Step {stepIndex + 1}/{tour.steps.length}
                    </span>
                    {step.tag && <span className="text-[9px] font-medium text-slate-400 bg-slate-800 rounded-full px-2 py-0.5">{step.tag}</span>}
                    {pitchMode && !pitchPaused && <span className="text-[9px] text-indigo-400 animate-pulse">Auto-advancing...</span>}
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-0.5">{step.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{step.description}</p>
                </div>
                <button onClick={endTour} className="text-slate-500 hover:text-slate-300 transition-colors p-1 shrink-0" data-testid="tour-end-btn"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <button onClick={goPrev} disabled={stepIndex === 0} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="tour-prev-btn"><ChevronLeft className="w-4 h-4" />Back</button>
                <div className="flex items-center gap-2">
                  {pitchMode && (
                    <button onClick={() => setPitchPaused(!pitchPaused)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="pitch-pause-btn">
                      {pitchPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}{pitchPaused ? 'Resume' : 'Pause'}
                    </button>
                  )}
                  <button onClick={endTour} className="text-[10px] text-slate-500 hover:text-slate-300 px-2 py-1" data-testid="tour-skip-btn">End</button>
                  <Button size="sm" onClick={goNext} className="bg-blue-600 hover:bg-blue-500 text-white gap-1 rounded-lg text-xs" data-testid="tour-next-btn">
                    {stepIndex === tour.steps.length - 1 ? 'Finish' : 'Next'}{stepIndex < tour.steps.length - 1 && <ChevronRight className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
}

export function StartDemoButton({ variant = "default", className = "" }) {
  const { startTour } = useTour();
  return (
    <button onClick={() => startTour('flagship')} data-testid="start-demo-btn"
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${
        variant === "landing" ? "bg-white text-slate-900 hover:bg-slate-100 shadow-sm border border-slate-200" : "bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
      } ${className}`}>
      <Play className="w-4 h-4" />Start Live Demo
    </button>
  );
}

export function DemoPathSelector() {
  const { startTour, startPitch } = useTour();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="demo-path-selector-btn"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all">
        <Layers className="w-3.5 h-3.5" />Demo paths
      </button>
      {open && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-slate-900 px-5 py-4"><h3 className="text-base font-semibold text-white">Choose a demo path</h3><p className="text-xs text-slate-400">Each path tells a different product story.</p></div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {Object.values(tourPaths).map(t => (
                <button key={t.id} onClick={() => { setOpen(false); startTour(t.id); }} className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                  <div className="flex items-center justify-between"><p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600">{t.title}</p><Badge className="text-[9px] bg-slate-100 text-slate-600">{t.steps.length} steps</Badge></div>
                  <p className="text-xs text-slate-500 mt-0.5">{t.summary}</p>
                </button>
              ))}
              <button onClick={() => { setOpen(false); startPitch(); }} className="w-full text-left p-3 rounded-xl border-2 border-indigo-200 bg-indigo-50/50 hover:border-indigo-300 transition-all group">
                <div className="flex items-center gap-2"><Play className="w-4 h-4 text-indigo-600" /><p className="text-sm font-bold text-indigo-800">Pitch Mode (auto-play)</p></div>
                <p className="text-xs text-indigo-600 mt-0.5">Auto-runs the flagship story for investor meetings.</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
